import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { type IncidentRow, type RiskCaseRow, type IncidentDetail, type EvidenceRefView } from '@vello/api-client';
import { incidentNextStatuses, riskCaseNextStatuses, formatMarketInstant, marketTimeZone, screenAllowed } from '../../src/index';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { PersonName } from '../components/person-name';
import { IdRef } from '../components/code-ref';
import { DrawerStack } from '../components/drawer-stack';
import { CentralLink } from '../components/central-link';
import { useRouter } from '../router';
import { useScopeZone } from '../components/scope-zone';
import { MarketField } from '../components/market-field';
import { AUTO_REFRESH_MS, useAutoRefresh, Badge, Button, Drawer, DrawerBody, DrawerFooter, DrawerHeader, DrawerSection, Field, KeyValueList, Notice, PageHeader, Panel, SegmentedControl, Select, Table, TextInput, EmptyState, Row, Stack } from '@vello/web-theme';
import { useVocab } from '../i18n/vocab';

/**
 * Segurança e risco (D318) — the operator's safety + risk desk over the real S096/M14 surfaces: the per-market
 * **safety queue** (`GET /v1/safety/incidents`) with the incident detail (`GET /v1/safety/incidents/:id`), its
 * encrypted **evidence references** (`GET /v1/safety-evidence?incidentId=` — never the media) with legal hold /
 * release, and the audited status transition (`POST …/transition`); plus the per-market **risk-review queue**
 * (`GET /v1/risk/cases`) with its review/appeal transition. The next statuses offered come from the real
 * `@vello/safety` / `@vello/risk` edge tables, so the console never offers a move the server would refuse.
 * Queues are per-market and fail-closed empty without one (the server contract).
 */
const SEVERITY_TONE: Readonly<Record<string, 'neutral' | 'success' | 'warning' | 'danger'>> = {
  LOW: 'neutral',
  MEDIUM: 'neutral',
  HIGH: 'warning',
  CRITICAL: 'danger',
};
const STATUS_TONE: Readonly<Record<string, 'neutral' | 'success' | 'warning' | 'danger'>> = {
  OPEN: 'warning',
  TRIAGED: 'neutral',
  IN_REVIEW: 'neutral',
  ESCALATED: 'danger',
  RESOLVED: 'success',
  CLOSED: 'neutral',
  UNDER_REVIEW: 'neutral',
  CONFIRMED: 'danger',
  DISMISSED: 'success',
  APPEALED: 'warning',
  OVERTURNED: 'success',
  UPHELD: 'danger',
};

type Selection = { readonly kind: 'incident'; readonly row: IncidentRow } | { readonly kind: 'risk'; readonly row: RiskCaseRow };

export function SafetyScreen(): ReactElement {
  const { runtime, scope, markets, grants } = useAdmin();
  const { navigate } = useRouter();
  const { t, lang } = useT();
  const { v, fail } = useVocab();
  const statusId = (kind: 'incident' | 'risk'): string => (kind === 'incident' ? 'safety.incident_status' : 'risk.risk_case_status');
  const { zone } = useScopeZone(); // D420: dates in the scope's market zone (A-18)
  const [list, setList] = useState<'incidents' | 'risk'>('incidents');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [market, setMarket] = useState(scope.current.city ?? '');
  const [loadedMarket, setLoadedMarket] = useState<string | undefined>(undefined);
  const [incidents, setIncidents] = useState<readonly IncidentRow[] | undefined>(undefined);
  const [cases, setCases] = useState<readonly RiskCaseRow[] | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);

  const [selected, setSelected] = useState<Selection | undefined>(undefined);
  const [detail, setDetail] = useState<IncidentDetail | undefined>(undefined);
  const [evidence, setEvidence] = useState<readonly EvidenceRefView[]>([]);
  const [to, setTo] = useState('');
  const [reasonCode, setReasonCode] = useState('');
  const [note, setNote] = useState('');
  const [holdCase, setHoldCase] = useState('');
  const [msg, setMsg] = useState<{ tone: 'success' | 'danger'; text: string } | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const readSeq = useRef(0);

  const load = useCallback(
    async (m: string): Promise<void> => {
      const seq = ++readSeq.current;
      setLoadedMarket(m);
      const [inc, risk] = await Promise.all([runtime.cases.listIncidents({ market: m, limit: 100 }), runtime.cases.listRiskCases({ market: m, limit: 100 })]);
      if (seq !== readSeq.current) return;
      setIncidents((prev) => (inc.ok ? inc.value.incidents : (prev ?? [])));
      setCases((prev) => (risk.ok ? risk.value.cases : (prev ?? [])));
      if (!inc.ok) setError(t('saf.error.incidents', { code: fail(inc.error) }));
      else if (!risk.ok) setError(t('saf.error.risk', { code: fail(risk.error) }));
      else setError(undefined);
    },
    [runtime, t],
  );

  // The queues follow the header scope's market by themselves (the skeleton returns only on a market change) and stay
  // current in the background; there is no "load" button.
  useEffect(() => {
    setIncidents(undefined);
    setCases(undefined);
    setError(undefined);
    if (market.trim() !== '') void load(market.trim());
  }, [market, load]);
  useAutoRefresh(() => load(market.trim()), { intervalMs: AUTO_REFRESH_MS.queue, enabled: market.trim() !== '' });

  const openSeq = useRef(0);
  const openIncident = async (row: IncidentRow): Promise<void> => {
    const seq = ++openSeq.current;
    setSelected({ kind: 'incident', row });
    setDetail(undefined);
    setEvidence([]);
    setMsg(undefined);
    setTo('');
    setReasonCode('');
    setHoldCase(row.incidentId);
    setDrawerOpen(true);
    const [d, ev] = await Promise.all([runtime.safety.getIncident(row.incidentId), runtime.safetyEvidence.listByIncident(row.incidentId)]);
    if (seq !== openSeq.current) return;
    setDetail(d.ok ? d.value : undefined);
    setEvidence(ev.ok ? ev.value.evidence : []);
  };

  const openRisk = (row: RiskCaseRow): void => {
    openSeq.current += 1;
    setSelected({ kind: 'risk', row });
    setDetail(undefined);
    setEvidence([]);
    setMsg(undefined);
    setTo('');
    setReasonCode('');
    setNote('');
    setDrawerOpen(true);
  };

  const applyTransition = async (): Promise<void> => {
    if (selected === undefined || to === '' || reasonCode.trim() === '') return;
    setBusy(true);
    if (selected.kind === 'incident') {
      const res = await runtime.cases.transitionIncident(selected.row.incidentId, { to, reasonCode: reasonCode.trim() });
      setBusy(false);
      if (res.ok) {
        setMsg({ tone: 'success', text: t('saf.transitioned', { status: v('safety.incident_status', res.value.status) }) });
        setSelected({ kind: 'incident', row: { ...selected.row, status: res.value.status } });
        setDetail((d) => (d !== undefined ? { ...d, status: res.value.status } : d));
        setTo('');
        if (loadedMarket !== undefined) await load(loadedMarket);
      } else setMsg({ tone: 'danger', text: t('ui.actionFailed', { code: fail(res.error) }) });
    } else {
      const res = await runtime.cases.transitionRiskCase(selected.row.caseId, { to, reasonCode: reasonCode.trim(), ...(note.trim() !== '' ? { note: note.trim() } : {}) });
      setBusy(false);
      if (res.ok) {
        setMsg({ tone: 'success', text: t('saf.transitioned', { status: v('risk.risk_case_status', to) }) });
        setSelected({ kind: 'risk', row: { ...selected.row, status: to } });
        setTo('');
        if (loadedMarket !== undefined) await load(loadedMarket);
      } else setMsg({ tone: 'danger', text: t('ui.actionFailed', { code: fail(res.error) }) });
    }
  };

  const toggleHold = async (ev: EvidenceRefView): Promise<void> => {
    setBusy(true);
    const res = ev.legalHold !== undefined ? await runtime.safetyEvidence.releaseLegalHold(ev.evidenceId) : await runtime.safetyEvidence.placeLegalHold(ev.evidenceId, holdCase.trim() || ev.incidentId || ev.evidenceId);
    setBusy(false);
    if (res.ok) {
      setEvidence((list) => list.map((x) => (x.evidenceId === ev.evidenceId ? res.value.evidence : x)));
      setMsg({ tone: 'success', text: t(ev.legalHold !== undefined ? 'saf.hold.released' : 'saf.hold.placed') });
    } else setMsg({ tone: 'danger', text: t('ui.actionFailed', { code: fail(res.error) }) });
  };

  const nextStatuses = selected === undefined ? [] : selected.kind === 'incident' ? incidentNextStatuses(selected.row.status) : riskCaseNextStatuses(selected.row.status);

  const incidentRow = selected?.kind === 'incident' ? selected.row : undefined;
  const rideOfIncident = incidentRow?.rideId;
  const fmtDate = (iso: string): string => formatMarketInstant(iso, marketTimeZone(market.trim(), markets), lang);

  return (
    <>
      <PageHeader title={t('saf.title')} subtitle={t('saf.subtitle')} />

      <Panel>
        <Stack gap={3}>
        <MarketField id="saf-market" value={market} onChange={setMarket} hintId="ui.market.hint" />
        {error !== undefined && <Notice tone="danger">{error}</Notice>}
        </Stack>
      </Panel>

      <SegmentedControl
        options={[{ id: 'incidents' as const, label: t('saf.incidents.title') }, { id: 'risk' as const, label: t('saf.risk.title') }]}
        value={list}
        onChange={setList}
        ariaLabel={t('saf.title')}
      />

      {list === 'incidents' ? (
        <Table
          columns={[
            { key: 'id', header: t('saf.col.incident'), render: (i: IncidentRow) => <IdRef value={i.incidentId} /> },
            { key: 'type', header: t('saf.col.type'), render: (i: IncidentRow) => v('safety.incident_type', i.type) },
            { key: 'sev', header: t('saf.col.severity'), render: (i: IncidentRow) => <Badge tone={SEVERITY_TONE[i.severity] ?? 'neutral'}>{v('safety.severity', i.severity)}</Badge> },
            { key: 'status', header: t('saf.col.status'), render: (i: IncidentRow) => <Badge tone={STATUS_TONE[i.status] ?? 'neutral'}>{v('safety.incident_status', i.status)}</Badge> },
            { key: 'ride', header: t('saf.col.ride'), render: (i: IncidentRow) => (i.rideId !== undefined ? <IdRef value={i.rideId} /> : '—') },
            { key: 'subject', header: t('saf.col.subject'), render: (i: IncidentRow) => <PersonName id={i.subjectId} /> },
            { key: 'ev', header: t('saf.col.evidence'), numeric: true, render: (i: IncidentRow) => String(i.evidenceCount) },
            { key: 'created', header: t('saf.col.created'), render: (i: IncidentRow) => <span className="tnum">{fmtDate(i.createdAt)}</span> },
          ]}
          rows={incidents ?? []}
          rowKey={(i) => i.incidentId}
          onRowClick={(i) => void openIncident(i)}
          {...(drawerOpen && selected?.kind === 'incident' ? { selectedKey: selected.row.incidentId } : {})}
          empty={<EmptyState title={incidents === undefined ? t('saf.prompt') : t('saf.incidents.empty')} />}
        />
      ) : (
        <Table
          columns={[
            { key: 'id', header: t('saf.col.case'), render: (c: RiskCaseRow) => <IdRef value={c.caseId} /> },
            { key: 'subject', header: t('saf.col.subject'), render: (c: RiskCaseRow) => <PersonName id={c.subjectId} /> },
            { key: 'action', header: t('saf.col.action'), render: (c: RiskCaseRow) => <Badge tone={c.action === 'SUSPEND' || c.action === 'BLOCK_CASH' ? 'danger' : 'warning'}>{v('risk.risk_action', c.action)}</Badge> },
            { key: 'status', header: t('saf.col.status'), render: (c: RiskCaseRow) => <Badge tone={STATUS_TONE[c.status] ?? 'neutral'}>{v('risk.risk_case_status', c.status)}</Badge> },
            { key: 'policy', header: t('saf.col.policy'), render: (c: RiskCaseRow) => <span className="tnum">{c.policyVersion}</span> },
          ]}
          rows={cases ?? []}
          rowKey={(c) => c.caseId}
          onRowClick={(c) => openRisk(c)}
          {...(drawerOpen && selected?.kind === 'risk' ? { selectedKey: selected.row.caseId } : {})}
          empty={<EmptyState title={cases === undefined ? t('saf.prompt') : t('saf.risk.empty')} />}
        />
      )}

      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} labelledBy="saf-drawer-title">
        {selected !== undefined && (
          <>
            <DrawerHeader
              titleId="saf-drawer-title"
              title={selected.kind === 'incident' ? t('saf.kind.incident') : t('saf.kind.risk')}
              actions={<Badge tone={STATUS_TONE[selected.row.status] ?? 'neutral'}>{v(statusId(selected.kind), selected.row.status)}</Badge>}
              onClose={() => setDrawerOpen(false)}
              closeLabel={t('ui.close')}
            />
            <DrawerBody>
              <DrawerStack>
                {selected.kind === 'incident' && <CentralLink kind="safety_incident" recordId={selected.row.incidentId} />}
                {selected.kind === 'incident' ? (
                  <KeyValueList
                    items={[
                      { label: t('saf.col.incident'), value: <IdRef value={selected.row.incidentId} copy full /> },
                      { label: t('saf.col.type'), value: v('safety.incident_type', detail?.type ?? selected.row.type) },
                      { label: t('saf.col.severity'), value: <Badge tone={SEVERITY_TONE[detail?.severity ?? selected.row.severity] ?? 'neutral'}>{v('safety.severity', detail?.severity ?? selected.row.severity)}</Badge> },
                      { label: t('saf.detail.reason'), value: v('ops.incident_reason', detail?.reasonCode ?? selected.row.reasonCode) },
                      ...(rideOfIncident !== undefined
                        ? [{
                            label: t('saf.col.ride'),
                            value: (
                              <Row gap={2} align="center" wrap>
                                <IdRef value={rideOfIncident} copy full />
                                {screenAllowed(grants, 'ride_detail') && <Button size="sm" variant="secondary" icon="arrowRight" onClick={() => navigate('ride_detail', { id: rideOfIncident })}>{t('saf.detail.openRide')}</Button>}
                              </Row>
                            ),
                          }]
                        : []),
                      { label: t('saf.col.subject'), value: <PersonName id={detail?.subjectId ?? selected.row.subjectId} /> },
                      ...(detail?.reporterId !== undefined ? [{ label: t('saf.detail.reporter'), value: <PersonName id={detail.reporterId} /> }] : []),
                      { label: t('saf.col.created'), value: fmtDate(selected.row.createdAt), mono: true },
                      { label: t('saf.detail.updated'), value: fmtDate(selected.row.updatedAt), mono: true },
                    ]}
                  />
                ) : (
                  <KeyValueList
                    items={[
                      { label: t('saf.col.case'), value: <IdRef value={selected.row.caseId} copy full /> },
                      { label: t('saf.col.subject'), value: <PersonName id={selected.row.subjectId} /> },
                      { label: t('saf.col.action'), value: <Badge tone={selected.row.action === 'SUSPEND' || selected.row.action === 'BLOCK_CASH' ? 'danger' : 'warning'}>{v('risk.risk_action', selected.row.action)}</Badge> },
                      { label: t('saf.col.policy'), value: selected.row.policyVersion, mono: true },
                    ]}
                  />
                )}

                {msg !== undefined && <Notice tone={msg.tone}>{msg.text}</Notice>}

                {selected.kind === 'incident' && (
                  <DrawerSection title={t('saf.evidence.title')}>
                    <Stack gap={3}>
                      <Field label={t('saf.field.holdCase')} htmlFor="saf-hold" hint={t('saf.field.holdCase.hint')}>
                        <TextInput id="saf-hold" className="tnum" value={holdCase} onChange={(e) => setHoldCase(e.target.value)} />
                      </Field>
                      <Table
                        columns={[
                          { key: 'id', header: t('saf.col.evidenceId'), render: (e: EvidenceRefView) => <IdRef value={e.evidenceId} /> },
                          { key: 'type', header: t('saf.col.contentType'), render: (e: EvidenceRefView) => v('safety.evidence_content_type', e.contentType) },
                          {
                            key: 'state',
                            header: t('saf.col.state'),
                            render: (e: EvidenceRefView) => (
                              <Row gap={1} wrap>
                                <Badge tone={e.state === 'DELETED' ? 'danger' : 'success'}>{v('safety.evidence_state', e.state)}</Badge>
                                {e.legalHold !== undefined && <Badge tone="warning">{t('saf.col.hold')}</Badge>}
                              </Row>
                            ),
                          },
                          { key: 'exp', header: t('saf.col.expires'), render: (e: EvidenceRefView) => <span className="tnum">{formatMarketInstant(e.expiresAt, zone, lang, 'date')}</span> },
                          {
                            key: 'act',
                            header: '',
                            render: (e: EvidenceRefView) => (
                              <Row justify="end">
                                {e.state !== 'DELETED' && (
                                  <Button size="sm" variant={e.legalHold !== undefined ? 'ghost' : 'secondary'} disabled={busy} onClick={() => void toggleHold(e)}>
                                    {e.legalHold !== undefined ? t('saf.hold.release') : t('saf.hold.place')}
                                  </Button>
                                )}
                              </Row>
                            ),
                          },
                        ]}
                        rows={evidence}
                        rowKey={(e) => e.evidenceId}
                        empty={<EmptyState title={t('saf.evidence.empty')} />}
                      />
                    </Stack>
                  </DrawerSection>
                )}

                <DrawerSection title={t('saf.transition.title')}>
                  {nextStatuses.length === 0 ? (
                    <Notice tone="neutral">{t('saf.transition.terminal')}</Notice>
                  ) : (
                    <Stack gap={3}>
                      <Field label={t('saf.field.to')} htmlFor="saf-to">
                        <Select id="saf-to" value={to} onChange={(e) => setTo(e.target.value)}>
                          <option value="">{t('saf.field.to.placeholder')}</option>
                          {nextStatuses.map((st) => (
                            <option key={st} value={st}>{v(statusId(selected.kind), st)}</option>
                          ))}
                        </Select>
                      </Field>
                      <Field label={t('saf.field.reasonCode')} htmlFor="saf-reason">
                        <TextInput id="saf-reason" value={reasonCode} placeholder={t('saf.field.reasonCode.placeholder')} onChange={(e) => setReasonCode(e.target.value)} />
                      </Field>
                      {selected.kind === 'risk' && (
                        <Field label={t('saf.field.note')} htmlFor="saf-note">
                          <TextInput id="saf-note" value={note} onChange={(e) => setNote(e.target.value)} />
                        </Field>
                      )}
                    </Stack>
                  )}
                </DrawerSection>
              </DrawerStack>
            </DrawerBody>
            {nextStatuses.length > 0 && (
              <DrawerFooter>
                <Button variant="primary" block disabled={busy || to === '' || reasonCode.trim() === ''} onClick={() => void applyTransition()}>{t('saf.transition.apply')}</Button>
              </DrawerFooter>
            )}
          </>
        )}
      </Drawer>
    </>
  );
}
